import React, { type ReactNode } from "react";
import { Loader2, LogIn, LogOut } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { GoogleConnection } from "@/contexts/GoogleConnectionContext";

export type GoogleService = "drive" | "gmail";

const SERVICE_DEFAULTS: Record<
  GoogleService,
  { title: string; connectedTitle: string; description: string }
> = {
  drive: {
    title: "Connect Google Drive",
    connectedTitle: "Google Drive",
    description:
      "Sign in to browse your Drive folders and import documents into your library.",
  },
  gmail: {
    title: "Connect Gmail",
    connectedTitle: "Gmail",
    description:
      "Sign in with Google to browse your inbox and analyze emails with AI agents. Google will open in this tab, then return you here.",
  },
};

type GoogleConnectCardProps = {
  connection: GoogleConnection;
  onConnect: () => void;
  onDisconnect: () => void;
  service?: GoogleService;
  title?: string;
  connectedTitle?: string;
  description?: string;
  icon?: ReactNode;
};

export const GoogleConnectCard = React.memo(function GoogleConnectCard({
  connection,
  onConnect,
  onDisconnect,
  service = "drive",
  title,
  connectedTitle,
  description,
  icon,
}: GoogleConnectCardProps) {
  const defaults = SERVICE_DEFAULTS[service];
  const displayTitle = title ?? defaults.title;
  const displayConnectedTitle = connectedTitle ?? defaults.connectedTitle;
  const displayDescription = description ?? defaults.description;
  const isServiceConnected =
    service === "gmail" ? connection.gmailConnected : connection.driveConnected;

  if (isServiceConnected) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {icon}
              <div>
                <CardTitle className="text-base">{displayConnectedTitle}</CardTitle>
                <CardDescription>
                  {connection.profile?.email || "Connected"}
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onDisconnect}>
              <LogOut className="mr-2 h-4 w-4" />
              Disconnect
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="text-base">{displayTitle}</CardTitle>
        </div>
        <CardDescription>{displayDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        {connection.error && (
          <p className="mb-3 text-sm text-destructive">{connection.error}</p>
        )}
        <Button
          onClick={onConnect}
          disabled={connection.isLoading}
        >
          {connection.isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="mr-2 h-4 w-4" />
          )}
          Sign in with Google
        </Button>
      </CardContent>
    </Card>
  );
});

/** @deprecated Use GoogleConnectCard */
export const GoogleDriveConnectCard = GoogleConnectCard;
